import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Loader2 } from 'lucide-react';
import FilterBuilder from '../../../components/FilterBuilder';
import AppContext from '../../../contexts/AppContext';

export const QuotaEditDialog = ({ open, quota, onClose, onQuotaUpdated }) => {
  const { serviceDesk, currentUser } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serviceDesksWithRequestTypes, setServiceDesksWithRequestTypes] = useState([]);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  // Fetch service desks with request types
  React.useEffect(() => {
    if (open) {
      fetchWithAuth('/service-desks/with-request-types')
        .then(res => res.json())
        .then(json => {
          if (json.success) {
            setServiceDesksWithRequestTypes(json.data);
          }
        })
        .catch(err => console.error('Error fetching service desks with request types:', err));
    }
  }, [open]);

  const [formData, setFormData] = useState({
    enabled: true,
    serviceDeskId: '',
    requestTypeId: '',
    windowType: 'month',
    windowDays: '',
    windowFrom: '',
    windowTo: '',
    filters: {
      project: '',
      requestType: '',
      customFields: []
    },
    limit: '',
    behavior: 'BLOCK',
    message: ''
  });

  // Populate form when quota changes
  useEffect(() => {
    if (quota && open) {
      // Use structured filters if available, otherwise provide empty structure
      const filters = quota.filters || {
        project: '',
        requestType: '',
        customFields: []
      };

      setFormData({
        enabled: quota.enabled,
        serviceDeskId: quota.scope.serviceDeskId.toString(),
        requestTypeId: quota.scope.requestTypeId.toString(),
        windowType: quota.window.type,
        windowDays: quota.window.days?.toString() || '',
        windowFrom: quota.window.from || '',
        windowTo: quota.window.to || '',
        filters,
        limit: quota.limit.toString(),
        behavior: quota.onExceed.behavior,
        message: quota.onExceed.message
      });
      setError(null);
    }
  }, [quota, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleFiltersChange = (filters) => {
    setFormData(prev => ({ ...prev, filters }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Build window object based on type
      let window;
      switch (formData.windowType) {
        case 'month':
          window = { type: 'month' };
          break;
        case 'isoQuarter':
          window = { type: 'isoQuarter' };
          break;
        case 'rollingDays':
          const days = parseInt(formData.windowDays);
          if (isNaN(days) || days <= 0) {
            throw new Error('Le nombre de jours doit être un nombre positif');
          }
          window = { type: 'rollingDays', days };
          break;
        case 'fixed':
          if (!formData.windowFrom || !formData.windowTo) {
            throw new Error('Les dates de début et fin sont requises pour une période fixe');
          }
          window = {
            type: 'fixed',
            from: formData.windowFrom,
            to: formData.windowTo
          };
          break;
        case 'none':
          window = { type: 'none' };
          break;
        default:
          throw new Error('Type de période invalide');
      }

      // Parse serviceDeskId and requestTypeId
      const serviceDeskId = parseInt(formData.serviceDeskId);
      const requestTypeId = parseInt(formData.requestTypeId);
      const limit = parseInt(formData.limit);

      if (isNaN(serviceDeskId)) {
        throw new Error('Service Desk ID invalide');
      }
      if (isNaN(requestTypeId)) {
        throw new Error('Request Type ID invalide');
      }
      if (isNaN(limit) || limit < 0) {
        throw new Error('La limite doit être un nombre positif');
      }

      // Send filters directly - backend will build JQL
      const payload = {
        enabled: formData.enabled,
        scope: {
          serviceDeskId,
          requestTypeId
        },
        window,
        filters: formData.filters, // Backend will build JQL from this
        limit,
        onExceed: {
          behavior: formData.behavior,
          message: formData.message.trim()
        }
      };

      const response = await fetchWithAuth(`/admin/quotas/${quota.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Erreur lors de la mise à jour du quota');
      }

      if (json.success) {
        onQuotaUpdated();
      } else {
        throw new Error(json.error || 'Erreur lors de la mise à jour du quota');
      }
    } catch (err) {
      console.error('Error updating quota:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!quota) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier le quota</DialogTitle>
          <DialogDescription>
            Modifiez les paramètres de la règle de quota
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="enabled">Statut</Label>
            <Select value={formData.enabled.toString()} onValueChange={(value) => handleChange('enabled', value === 'true')}>
              <SelectTrigger id="enabled">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Actif</SelectItem>
                <SelectItem value="false">Inactif</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="serviceDeskId">Service Desk *</Label>
            {isSuperAdmin ? (
              <select
                id="serviceDeskId"
                value={formData.serviceDeskId}
                onChange={(e) => {
                  const sdId = e.target.value;
                  const selectedSd = serviceDesksWithRequestTypes.find(sd => sd.id.toString() === sdId);
                  setFormData(prev => ({
                    ...prev,
                    serviceDeskId: sdId,
                    requestTypeId: '', // Reset request type when service desk changes
                    filters: {
                      ...prev.filters,
                      project: selectedSd?.projectKey || ''
                    }
                  }));
                }}
                className="w-full px-3 py-2 border rounded-md text-sm"
                required
              >
                <option value="">Sélectionner un service desk...</option>
                {serviceDesksWithRequestTypes.map((sd) => (
                  <option key={sd.id} value={sd.id}>
                    {sd.name} ({sd.projectKey})
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id="serviceDeskId"
                value={serviceDesk?.config?.name ? `${serviceDesk.config.name} (${serviceDesk.config.projectKey})` : ''}
                disabled
                className="bg-gray-100"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="requestTypeId">Type de demande *</Label>
            <select
              id="requestTypeId"
              value={formData.requestTypeId}
              onChange={(e) => handleChange('requestTypeId', e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm"
              required
              disabled={!formData.serviceDeskId}
            >
              <option value="">Sélectionner un type de demande...</option>
              {serviceDesksWithRequestTypes
                .find(sd => sd.id.toString() === formData.serviceDeskId)
                ?.requestTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name}
                  </option>
                ))}
            </select>
            {!formData.serviceDeskId && (
              <p className="text-xs text-muted-foreground">
                Sélectionnez d'abord un service desk
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="windowType">Type de période *</Label>
            <Select value={formData.windowType} onValueChange={(value) => handleChange('windowType', value)}>
              <SelectTrigger id="windowType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Mensuel</SelectItem>
                <SelectItem value="isoQuarter">Trimestriel</SelectItem>
                <SelectItem value="rollingDays">Jours glissants</SelectItem>
                <SelectItem value="fixed">Période fixe</SelectItem>
                <SelectItem value="none">Aucune limite</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.windowType === 'rollingDays' && (
            <div className="space-y-2">
              <Label htmlFor="windowDays">Nombre de jours *</Label>
              <Input
                id="windowDays"
                type="number"
                value={formData.windowDays}
                onChange={(e) => handleChange('windowDays', e.target.value)}
                required
              />
            </div>
          )}

          {formData.windowType === 'fixed' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="windowFrom">Date de début *</Label>
                <Input
                  id="windowFrom"
                  type="date"
                  value={formData.windowFrom}
                  onChange={(e) => handleChange('windowFrom', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="windowTo">Date de fin *</Label>
                <Input
                  id="windowTo"
                  type="date"
                  value={formData.windowTo}
                  onChange={(e) => handleChange('windowTo', e.target.value)}
                  required
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Filtres</Label>
            <div className="border rounded-lg p-4 bg-gray-50">
              {serviceDesksWithRequestTypes.length > 0 ? (
                <FilterBuilder
                  filters={formData.filters}
                  onChange={handleFiltersChange}
                  serviceDeskConfig={
                    // Use selected service desk's config from loaded data
                    serviceDesksWithRequestTypes.find(sd => sd.id.toString() === formData.serviceDeskId)
                  }
                  hideServiceDeskField={true}
                />
              ) : (
                <div className="text-sm text-gray-500 py-4 text-center">
                  Chargement des configurations...
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Configurez les filtres pour les demandes soumises au quota
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="limit">Limite *</Label>
            <Input
              id="limit"
              type="number"
              value={formData.limit}
              onChange={(e) => handleChange('limit', e.target.value)}
              min="0"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="behavior">Action en cas de dépassement *</Label>
            <Select value={formData.behavior} onValueChange={(value) => handleChange('behavior', value)}>
              <SelectTrigger id="behavior">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BLOCK">Bloquer</SelectItem>
                <SelectItem value="WARN">Avertir</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              rows={2}
              required
            />
            <p className="text-xs text-muted-foreground">
              Message affiché à l'utilisateur lorsque le quota est atteint
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mettre à jour
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
